/**
 * The in-shell not-found state.
 *
 * Reuses the design's empty-state vocabulary rather than inventing an error page,
 * and it says which of the two things happened: a URL that was never a route, or a
 * module that this phase has not built yet. The distinction is what stops a
 * coordinator filing "the Testing screen is broken" when the truth is "the Testing
 * screen arrives in Phase 4".
 */

import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Compass } from 'lucide-react';
import { NAV_SECTIONS } from '../domain/nav';
import { EmptyState } from './ui';

export function NotFound(): React.JSX.Element {
  const location = useLocation();
  const parts = location.pathname.split('/').filter((part) => part !== '');
  const countryCode = parts[1] ?? '';
  const segment = parts[2] ?? '';

  const known = NAV_SECTIONS.flatMap((section) => section.items).find(
    (item) => item.path !== '' && item.path === segment,
  );

  return (
    <div className="px-6 pt-10">
      <EmptyState
        icon={<Compass aria-hidden="true" className="size-5" />}
        title={
          known === undefined
            ? 'This page does not exist'
            : `${known.label} is not built yet`
        }
        description={
          known === undefined
            ? 'The address does not match anything in Global One. Check the link, or go back to the dashboard.'
            : `${known.label} arrives in phase ${String(known.phase)}. Nothing is broken — the screen simply does not exist yet.`
        }
        action={
          countryCode === '' ? null : (
            <Link
              to={`/countries/${countryCode}`}
              className="mt-2 inline-flex h-9 items-center rounded-sm border border-line px-3 text-body font-medium text-indigo hover:bg-indigo-soft"
            >
              Back to dashboard
            </Link>
          )
        }
      />
    </div>
  );
}
